/**
 * WS11 — "Take your book" per-salon export orchestrator (brief 11; ADR-0012 client-book portability).
 *
 * GET /v1/gdpr/book/export runs under the CURRENT active tenant ONLY — it returns the session's own
 * sealed book in whatever salon the session is presently in. There is no cross-tenant export endpoint
 * (an in-place cross-tenant read would breach RLS — the only boundary). So to export the caller's
 * sealed book in salon X we must FIRST become the session-in-X (auth.switchSalon clears the cache),
 * run the live export, hand the bundle to the browser as a file, and THEN restore the original tenant
 * context — even if the export throws. That restore is the whole point of the try/FINALLY here: a
 * failed or half-finished export must never strand the operator in a salon they didn't choose to be in.
 *
 * This is a PURE orchestrator (no React) so the switch → export → download → restore path is unit-
 * testable end to end with injected fakes. The console wires the real auth.switchSalon, useExportBook,
 * and downloadJson into it. It performs NO money/availability math and never merges or recomputes the
 * union — it only moves bytes the server produced.
 */

/**
 * Thrown when the export itself completed (the bytes are with the operator) but the switch BACK to the
 * original tenant failed — so the operator is stranded in the target salon, NOT "back where they
 * started". The caller distinguishes this from an export failure to show an honest toast (the feature's
 * whole contract is a clean exit that never silently strands the operator). Carries the home salon id so
 * the caller can name where to return to, and the underlying restore error as `cause`.
 */
export class BookExportRestoreError extends Error {
  readonly homeSalonId: string;
  /** True when the export itself succeeded before the restore failed (the bundle reached the browser). */
  readonly exportSucceeded: boolean;
  constructor(homeSalonId: string, exportSucceeded: boolean, cause: unknown) {
    super('Exported, but could not switch you back to your home salon');
    this.name = 'BookExportRestoreError';
    this.homeSalonId = homeSalonId;
    this.exportSucceeded = exportSucceeded;
    this.cause = cause;
  }
}

export interface BookExportDeps<TBundle> {
  /** The salon tenant currently active (user.salonId) — restored in the FINALLY. */
  currentSalonId: string;
  /** The salon we want to export from (a renter residency's salonId). */
  targetSalonId: string;
  /** auth.switchSalon — changes the session's tenant context and clears the query cache. */
  switchSalon: (salonId: string) => Promise<void>;
  /** useExportBook().mutateAsync — the live GET /gdpr/book/export under the active tenant. */
  exportBook: () => Promise<TBundle>;
  /** features/gdpr/gdpr-download.downloadJson — serialise the bundle to a file. */
  download: (bundle: TBundle) => void;
}

/**
 * Switch into the target salon (unless already there), run the live sealed-book export, download it,
 * then ALWAYS switch back to where we started. The restore runs in a `finally`, so a thrown export
 * (e.g. the network dropped mid-flight) still returns the operator to their original context before
 * the error propagates. Returns the exported bundle on success; rethrows on failure (the caller shows
 * the toast / busy state). When already in the target salon, no switching happens at all.
 */
export async function runBookExport<TBundle>(deps: BookExportDeps<TBundle>): Promise<TBundle> {
  const { currentSalonId, targetSalonId, switchSalon, exportBook, download } = deps;
  const mustSwitch = targetSalonId !== currentSalonId;

  if (mustSwitch) await switchSalon(targetSalonId);

  let bundle: TBundle | undefined;
  let exportError: unknown;
  let exported = false;
  try {
    bundle = await exportBook();
    download(bundle);
    exported = true;
  } catch (err) {
    exportError = err;
  }

  // Restore the original tenant context whether the export succeeded or threw, but only if we actually
  // left it — switching back when we never switched would needlessly clear the cache. If the restore
  // ITSELF fails, the operator is now stranded in the target salon (NOT "back where they started"), so
  // we surface a distinct BookExportRestoreError rather than letting the generic catch claim a clean
  // exit. A restore failure takes precedence over an export failure: stranded is the worse, truer state.
  if (mustSwitch) {
    try {
      await switchSalon(currentSalonId);
    } catch (restoreErr) {
      throw new BookExportRestoreError(currentSalonId, exported, restoreErr);
    }
  }

  if (exportError) throw exportError;
  return bundle as TBundle;
}
